import React, { useState } from "react";

// components
import InvoiceForm from "../invoice-form/invoice-form.component";
import FormError from "../form-error/form-error.component";
import InlineNotice from "../inline-notice/inline-notice.component";
import BillSummaryCard from "../bill-summary-card/bill-summary-card.component";
import PrintOptionsDialog from "../print-options-dialog/print-options-dialog.component";

// global functions
import { isEmptyObject } from "../../global/function";

// queries
import { usePrintDraft } from "../../queries/bill.queries";
import type { PrintSize } from "../../queries/bill.queries";
import { useLastPrintSize } from "../../hooks/useLastPrintSize";

// types
import type { Draft } from "../../queries/drafts.queries";
import type { InvoiceFormValues } from "../invoice-form/invoice-form.types";

interface DraftPreviewPanelProps {
	selectedInvoice: Draft | Record<string, never>;
	recentBillDate: string | false;
	isUpdateError?: boolean;
	updateError?: unknown;
	printDialogOpen: boolean;
	onPrintDialogChange: (open: boolean) => void;
	onFailedToReady: (
		draft: Partial<InvoiceFormValues> & Record<string, unknown>,
		clickedButton: string,
	) => Promise<unknown>;
}

// Extracted from ready-draft-list.component.jsx's SideModal content — the
// preview (or, for a failed draft, the inline edit form) is a self-contained
// subsection. Action buttons (Print/Revert/Create Bill) live in the
// StructuredSideModal `actions` slot owned by ready-draft-list.
export default function DraftPreviewPanel({
	selectedInvoice,
	recentBillDate,
	isUpdateError,
	updateError,
	printDialogOpen,
	onPrintDialogChange,
	onFailedToReady,
}: DraftPreviewPanelProps) {
	const [printSuccessNotice, setPrintSuccessNotice] = useState<string | null>(
		null,
	);
	const [lastPrintSize, setLastPrintSize] = useLastPrintSize();
	const printDraftMutation = usePrintDraft();

	// Drafts have no post-print count-increment today (neither this panel nor
	// ready-draft-list ever called useIncrementPrintCount for a draft) — this
	// intentionally doesn't add one.
	const handlePrintConfirm = async (size: PrintSize) => {
		const draft = selectedInvoice as Draft;
		if (!draft?.draft_id) return;
		try {
			await printDraftMutation.mutateAsync({
				draftId: draft.draft_id,
				size,
			});
			setLastPrintSize(size);
			onPrintDialogChange(false);
			setPrintSuccessNotice("PDF opened in a new tab.");
		} catch {
			// PrintOptionsDialog's own FormError (bound to printDraftMutation) shows the message.
		}
	};

	return (
		<>
			<FormError
				isError={isUpdateError}
				error={updateError}
				fallback="Something went wrong. Please try again."
			/>
			<InlineNotice
				message={printSuccessNotice}
				type="success"
				autoHideMs={4000}
				onDismiss={() => setPrintSuccessNotice(null)}
			/>
			<div className="modal-body">
				{!isEmptyObject(selectedInvoice) && (
					<>
						{selectedInvoice?.draft_bill_status === "failed" ? (
							<div style={{ padding: "16px" }}>
								<div className="draft-form-header">
									<h2 className="form-header-text">
										Failed Invoice Draft
									</h2>
									{recentBillDate && (
										<div className="form-header-subtext">
											<div className="label">
												Last Bill Date:
											</div>
											<div className="label-info">
												{recentBillDate}
											</div>
										</div>
									)}
								</div>
								<div className="header-divider"></div>
								<InvoiceForm
									formType="individual"
									draftSave={onFailedToReady}
									currentDraft={
										selectedInvoice as unknown as Partial<InvoiceFormValues> &
											Record<string, unknown>
									}
									onSetCurrent={() => {}}
									onUnsavedChanges={() => {}}
								/>
							</div>
						) : (
							(() => {
								const draft = selectedInvoice as Draft;
								return (
									<BillSummaryCard
										documentLabel={`Draft #${draft.invoice_number ?? draft.draft_id}`}
										date={draft.invoice_date}
										dateNepali={draft.invoice_date_nepali}
										salesDate={draft.sales_date}
										buyerName={draft.buyer_name}
										buyerPan={draft.buyer_pan}
										customerMobile={
											draft.customer_mobile ??
											draft.customer_info?.customer_mobile
										}
										paymentMode={draft.payment_mode}
										billedItems={draft.billed_items}
										subTotal={draft.sub_total}
										totalSales={draft.total_sales}
										discount={
											(draft.non_taxable_discount ?? 0) +
											(draft.taxable_discount ?? 0)
										}
										serviceCharge={
											(draft.non_taxable_service_charge ??
												0) +
											(draft.taxable_service_charge ?? 0)
										}
										nonTaxableAmount={draft.non_taxable_amt}
										taxableAmount={
											draft.esf
												? draft.amount_for_esf
												: draft.taxable_sales_vat
										}
										vat={draft.vat}
										esf={draft.esf}
									/>
								);
							})()
						)}
					</>
				)}
			</div>
			<PrintOptionsDialog
				open={printDialogOpen}
				onOpenChange={onPrintDialogChange}
				initialSize={lastPrintSize}
				isPending={printDraftMutation.isPending}
				isError={printDraftMutation.isError}
				error={printDraftMutation.error}
				onConfirm={handlePrintConfirm}
			/>
		</>
	);
}
